"use client";

/**
 * ONE PLACE THAT NOTICES THE SESSION HAS GONE STALE, AND FIXES IT.
 *
 * ── The report ──────────────────────────────────────────────────────────────
 * A user edited a project, the preview updated, and the workspace said
 *
 *   "Couldn't save your changes. They're running in the preview but aren't
 *    stored yet."
 *
 * They pressed Retry repeatedly. Nothing visible happened, and a hard refresh
 * both fixed the saving AND discarded every unsaved edit. The server log for
 * that session says why:
 *
 *   [auth] middleware session refresh failed { error_code: 400 }
 *
 * The refresh token was rejected, so every write answered 401. That is not a
 * save failure — the database was fine, the rows were fine, the project was
 * fine. It is a stale credential, and it has exactly one remedy: get a new one.
 *
 * ── Why Retry could not work ────────────────────────────────────────────────
 * Nothing in the client re-authenticated. `apiFetch` had no 401 branch at all,
 * and lib/db/save-failure-code.ts correctly refuses to retry a 401 — sending an
 * identical unauthenticated request again is pointless. So Retry re-sent a
 * request that could only fail, failed in about 200ms, and flipped a toast from
 * "syncing" to "couldn't save" fast enough to look like nothing had happened.
 * A full page load was the only thing that re-ran the auth flow, which is why
 * refreshing "fixed" it — and why it cost the user their work.
 *
 * ── What this is ───────────────────────────────────────────────────────────
 * The missing remedy, in one place. A 401 asks this module for a fresh session
 * once; if it gets one, the request is sent again and the user never learns
 * anything happened. If it does not, the failure is reported as what it is —
 * `session_expired` — so the UI can offer signing in rather than a Retry button
 * that cannot succeed.
 *
 * SHARED, NOT PER-CALLER. A stale session fails every in-flight request at
 * once — a save, a heartbeat, a credits poll — and each one asking for its own
 * refresh would send a burst of refresh calls with the same token, which is a
 * good way to have the provider start rejecting them. One in-flight refresh
 * serves everyone waiting on it.
 */

import { createClient } from "@/lib/supabase/client";

/** The refresh currently in flight, shared by every 401 that arrives during it. */
let inFlight: Promise<boolean> | null = null;

/**
 * Ask for a fresh session. Resolves true when the client holds a valid one.
 *
 * Never throws: a caller in a `catch` for a 401 has a worse failure to report
 * than this one, and an exception here would replace an accurate
 * "your session expired" with an incidental Supabase message.
 */
export function recoverSession(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const { data, error } = await createClient().auth.refreshSession();
      if (error || !data.session) {
        console.warn(
          `[session-recovery] refresh failed: ${error?.message ?? "no session returned"}`,
        );
        return false;
      }
      return true;
    } catch (err) {
      console.warn("[session-recovery] refresh threw:", err);
      return false;
    }
  })();

  // Cleared however it settles, so the NEXT 401 after a failed refresh gets a
  // real attempt instead of a cached "no". A session can come back — the user
  // may have signed in in another tab while this one sat on an error.
  return inFlight.finally(() => {
    inFlight = null;
  });
}
